<template>
  <v-dialog v-if="featureEnabled" :value="open" persistent width="520" @keydown.esc="handleCancel">
    <v-card
      class="efld-card"
      role="dialog"
      aria-modal="true"
      aria-labelledby="efld-title"
      data-testid="edit-family-level-dialog"
    >
      <div class="efld-header">
        <span class="efld-featured-icon" data-testid="efld-featured-icon">
          <svg
            class="efld-edit-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <v-spacer />
        <button
          type="button"
          class="efld-close"
          aria-label="Close Edit Level dialog"
          data-testid="efld-close"
          :disabled="busy"
          @click="handleCancel"
        >
          <svg
            class="efld-close__icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M18 6 6 18M6 6l12 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <h2 id="efld-title" class="efld-title" data-testid="efld-title">Edit Level</h2>

      <div class="efld-body">
        <div class="efld-field">
          <label class="efld-label" for="efld-name-input" data-testid="efld-name-label">
            Name
            <span class="efld-required" aria-hidden="true">*</span>
          </label>
          <input
            id="efld-name-input"
            v-model="localName"
            class="efld-input"
            :class="{ 'efld-input--error': nameError }"
            type="text"
            autocomplete="off"
            required
            :aria-required="true"
            :aria-invalid="nameError ? 'true' : 'false'"
            :disabled="busy"
            data-testid="efld-name-input"
          />
          <p v-if="nameError" class="efld-error" role="alert" data-testid="efld-name-error">
            {{ nameError }}
          </p>
        </div>

        <div class="efld-field">
          <label class="efld-label" for="efld-descriptor-input" data-testid="efld-descriptor-label">
            Descriptor
          </label>
          <textarea
            id="efld-descriptor-input"
            v-model="localDescriptor"
            class="efld-input efld-textarea"
            :class="{ 'efld-input--error': descriptorError }"
            rows="2"
            :aria-required="false"
            :aria-invalid="descriptorError ? 'true' : 'false'"
            :disabled="busy"
            data-testid="efld-descriptor-input"
          />
          <p
            v-if="descriptorError"
            class="efld-error"
            role="alert"
            data-testid="efld-descriptor-error"
          >
            {{ descriptorError }}
          </p>
        </div>
      </div>

      <div class="efld-footer">
        <button
          type="button"
          class="efld-btn efld-btn--secondary"
          data-testid="efld-cancel"
          :disabled="busy"
          @click="handleCancel"
        >
          Cancel
        </button>
        <button
          type="button"
          class="efld-btn efld-btn--primary"
          :class="{ 'efld-btn--disabled': saveDisabled }"
          :disabled="saveDisabled"
          :aria-busy="busy ? 'true' : 'false'"
          data-testid="efld-save"
          @click="handleSave"
        >
          <span
            v-if="busy"
            class="efld-spinner"
            aria-hidden="true"
            data-testid="efld-save-spinner"
          />
          <span>{{ busy ? "Saving" : "Save Level" }}</span>
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "EditFamilyLevelDialog",
};
</script>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";

import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";
import { useOrgObjectNameValidation } from "@/composables/useOrgObjectNameValidation";

/**
 * ER-11653 — EditFamilyLevelDialog (Organization > Job Families, Levels table row
 * ⋯ > Edit).
 *
 * Untitled-UI modal, sibling of AddFamilyLevelDialog (ER-11644): same two fields
 * (Name, Descriptor), same layout — only the title ("Edit Level") and the pre-filled
 * state differ. Opened pre-filled with the Level's committed `name`/`descriptor`;
 * the ordinal is never shown or editable here (position changes only by dragging the
 * row in JobFamilyLevelsTable). Name uniqueness within the Job Family is checked
 * inline through useOrgObjectNameValidation (ER-11901 / MDP-608).
 *
 * Controlled, presentational component: the parent owns `open` (v-model:open) and
 * the committed `level`. Local edit state is seeded from `level` whenever the dialog
 * opens. Save Level stays disabled until Name is non-empty AND at least one field
 * differs from the committed value — a no-op save is never allowed through.
 * Descriptor is optional (ER-11790); leaving it blank is a valid edit.
 * `busy` disables the fields/buttons and shows a spinner while the parent's save
 * request is in flight; `errors` surfaces server-side field errors (keyed `name` /
 * `descriptor`) without closing the dialog. No API calls inside the component — the
 * owning screen performs the PATCH and re-opens/closes the dialog based on the
 * result.
 *
 * Shipped behind the linked idea flag (idea-MDP-581), the same kill switch gating
 * the rest of the Job Family feature.
 */
const props = defineProps({
  /** Controls dialog visibility. Parent uses v-model:open. */
  open: {
    type: Boolean,
    default: false,
  },
  /** The Level being edited: committed { id, name, descriptor }. */
  level: {
    type: Object,
    required: true,
  },
  /** Id of the Job Family the Level belongs to (scopes the name check). */
  familyId: {
    type: [String, Number],
    default: null,
  },
  /** True while the parent's save request is in flight. */
  busy: {
    type: Boolean,
    default: false,
  },
  /** Server-side field errors, keyed by `name` / `descriptor`. */
  errors: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["update:open", "save", "cancel"]);

const featureEnabled = ref(false);
const proxy = getCurrentInstance()?.proxy;

const localName = ref("");
const localDescriptor = ref("");

function resetFromLevel() {
  localName.value = props.level?.name ?? "";
  localDescriptor.value = props.level?.descriptor ?? "";
}

if (props.open) {
  resetFromLevel();
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      resetFromLevel();
      nameValidation.error.value = "";
    }
  }
);

const nameValidation = useOrgObjectNameValidation({
  type: "job_family_level",
  excludeId: () => props.level?.id,
  jobFamilyId: () => props.familyId,
});

const trimmedName = computed(() => String(localName.value || "").trim());
const trimmedDescriptor = computed(() => String(localDescriptor.value || "").trim());
const committedName = computed(() => String(props.level?.name || "").trim());
const committedDescriptor = computed(() => String(props.level?.descriptor || "").trim());

const hasName = computed(() => trimmedName.value.length > 0);
const hasChanges = computed(
  () =>
    trimmedName.value !== committedName.value ||
    trimmedDescriptor.value !== committedDescriptor.value
);

watch(trimmedName, (value) => {
  nameValidation.name.value = value === committedName.value ? "" : value;
});

const saveDisabled = computed(
  () =>
    props.busy ||
    !hasName.value ||
    !hasChanges.value ||
    nameValidation.checking.value ||
    Boolean(nameValidation.error.value)
);

const nameError = computed(() => props.errors?.name || nameValidation.error.value);
const descriptorError = computed(() => props.errors?.descriptor || "");

function handleSave() {
  if (saveDisabled.value) {
    return;
  }
  emit("save", {
    id: props.level?.id,
    name: trimmedName.value,
    descriptor: trimmedDescriptor.value,
  });
}

function handleCancel() {
  if (props.busy) {
    return;
  }
  emit("update:open", false);
  emit("cancel");
}

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the dialog shut.
    featureEnabled.value = true;
  }
});
</script>

<style scoped>
.efld-card {
  background-color: #ffffff;
  border-radius: 12px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
  padding: 24px;
}

.efld-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.efld-featured-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  color: #667085;
}

.efld-edit-icon {
  display: block;
}

.efld-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #667085;
  cursor: pointer;
}

.efld-close:hover {
  background: #f2f4f7;
}

.efld-close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.efld-close:focus-visible,
.efld-input:focus-visible,
.efld-btn:focus-visible {
  outline: 2px solid #6941c6;
  outline-offset: 1px;
}

.efld-title {
  margin: 0 0 20px;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.efld-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.efld-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.efld-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.efld-required {
  color: #f04438;
}

.efld-input {
  box-sizing: border-box;
  width: 100%;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  line-height: 20px;
  color: #101828;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.efld-input::placeholder {
  color: #667085;
}

.efld-textarea {
  min-height: 58px;
  resize: vertical;
}

.efld-input--error {
  border-color: #fda29b;
}

.efld-error {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: #f04438;
}

.efld-footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 32px;
}

.efld-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  height: 44px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.efld-btn--secondary {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.efld-btn--primary {
  background: #6941c6;
  border: 1px solid #6941c6;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.efld-btn--disabled,
.efld-btn:disabled {
  background: #f2f4f7;
  border: none;
  color: #98a2b3;
  box-shadow: none;
  cursor: not-allowed;
}

.efld-spinner {
  width: 14px;
  height: 14px;
  margin-right: 8px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: efld-spin 0.7s linear infinite;
}

.efld-btn--secondary .efld-spinner {
  border-color: rgba(52, 64, 84, 0.35);
  border-top-color: #344054;
}

@keyframes efld-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
